import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface GalleryCarouselProps {
  slides: string[];
  alt?: string;
  onClickSlide?: (slideIndex: number) => void;
}

const GalleryCarousel = ({ slides, alt, onClickSlide }: GalleryCarouselProps) => {
  const [current, setCurrent] = useState(0);

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrent((c) => (c === 0 ? slides.length - 1 : c - 1));
  };

  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrent((c) => (c === slides.length - 1 ? 0 : c + 1));
  };

  return (
    <div className="relative group">
      <div
        className="rounded-sm border border-border overflow-hidden cursor-pointer transition-all duration-300 hover:brightness-75"
        onClick={() => onClickSlide?.(current)}
      >
        <img
          src={slides[current]}
          alt={alt ? `${alt} ${current + 1}` : ''}
          className="w-full h-auto transition-opacity duration-500"
          loading="lazy"
        />
      </div>

      {/* Navigation arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-background/70 backdrop-blur-sm text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-background/70 backdrop-blur-sm text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                i === current
                  ? 'bg-foreground scale-125'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/60'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default GalleryCarousel;
